import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { useProjectContext } from '@/project/ProjectContext';
import { ProjectGate } from '@/features/projects/ProjectGate';
import { useFeatureFlags } from '@/features/platform/useFeatureFlags';
import type { AudienceSummary } from '@/api/types';
import leadStyles from '@/features/leads/Leads.module.css';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { LoadingState } from '@/components/StateMessage/StateMessage';
import { DashboardKpiCard } from '@/features/dashboard/DashboardKpiCard';
import { ScrapeJobSourceFields } from '@/features/scraper/ScrapeJobSourceFields';
import { ScraperStatusBadge } from '@/features/scraper/ScraperStatusBadge';
import {
  buildScrapeJobConfig,
  initialScrapeJobFormState,
  isScrapeJobFormValid,
  type ScraperCapabilities,
} from '@/features/scraper/scrapeJobForm';
import dashboardStyles from './DashboardPage.module.css';
import projectStyles from './ProjectsPage.module.css';
import scraperStyles from './ScraperPage.module.css';

type ScrapeJobConfig = {
  source?: 'urls' | 'apify' | 'google_places' | 'google_serp';
  seed_urls?: string[];
  apify_preset?: string;
  segmentation?: { niche?: string | null; region?: string | null; queries?: string[] };
  apify_last_run_id?: string;
  apify_last_imported?: number;
  google_places_last_imported?: number;
  google_places_fetched?: number;
  google_serp_last_imported?: number;
  google_serp_rows?: number;
  google_serp_queries?: number;
  website_enrich_status?: string;
  website_enrich_updated?: number;
};

type ScrapeJob = {
  id: string;
  name: string;
  status: string;
  config: ScrapeJobConfig;
  respect_robots: boolean;
  rate_limit_ms: number;
  pending_count?: number;
  last_error?: string | null;
};

type ScrapeCandidate = {
  id: string;
  company: string | null;
  contact_name: string | null;
  email: string | null;
  website: string | null;
  source_url: string | null;
  confidence_score: string;
};

type DataHealthSummary = {
  total: number;
  stale: number;
  invalid_email: number;
  unknown_verify: number;
  pending_enrichment: number;
  pending_scrape: number;
};

function candidateScoreClass(score: string): string {
  const n = Number.parseFloat(score);
  if (!Number.isFinite(n)) return scraperStyles.scorePill;
  return `${scraperStyles.scorePill} ${n >= 0.65 ? scraperStyles.scoreHigh : ''}`.trim();
}

function jobSourceLabel(job: ScrapeJob): string {
  if (job.config?.source === 'google_places') return 'Google Places API';
  if (job.config?.source === 'google_serp') return 'Google Search SERP';
  if (job.config?.source === 'apify') return 'Apify';
  return 'Seed URLs';
}

export function ScraperPage() {
  const { selectedProject } = useProjectContext();
  const projectId = selectedProject?.id;
  const qc = useQueryClient();

  const [jobName, setJobName] = useState('');
  const [jobForm, setJobForm] = useState(() =>
    initialScrapeJobFormState({ seedUrls: 'https://example.com/contact\n' }),
  );
  const [runAfterCreate, setRunAfterCreate] = useState(true);
  const [blockDomain, setBlockDomain] = useState('');
  const [audienceId, setAudienceId] = useState('');
  const { isEnabled } = useFeatureFlags();
  const audiencesOn = isEnabled('audiences_v1');

  const audiencesQ = useQuery({
    queryKey: ['audiences', projectId],
    enabled: Boolean(projectId) && audiencesOn,
    queryFn: () =>
      apiFetch<{ audiences: AudienceSummary[] }>(`/api/audiences?project_id=${projectId}`),
  });

  const scrapeCapsQ = useQuery({
    queryKey: ['scraper-capabilities'],
    queryFn: () => apiFetch<ScraperCapabilities>('/api/scraper/capabilities'),
  });

  useEffect(() => {
    const caps = scrapeCapsQ.data;
    if (!caps) return;
    setJobForm((f) => {
      if (f.jobSource === 'google_places' && !caps.google_places?.configured) {
        if (caps.google_serp?.configured) return { ...f, jobSource: 'google_serp' };
        return { ...f, jobSource: caps.apify.configured ? 'apify' : 'urls' };
      }
      if (f.jobSource === 'google_serp' && !caps.google_serp?.configured) {
        if (caps.google_places?.configured) return { ...f, jobSource: 'google_places' };
        return { ...f, jobSource: caps.apify.configured ? 'apify' : 'urls' };
      }
      if (f.jobSource === 'apify' && !caps.apify.configured) {
        if (caps.google_places?.configured) return { ...f, jobSource: 'google_places' };
        if (caps.google_serp?.configured) return { ...f, jobSource: 'google_serp' };
        return { ...f, jobSource: 'urls' };
      }
      return f;
    });
  }, [scrapeCapsQ.data]);

  const catalogQ = useQuery({
    queryKey: ['scraper-acquisition-catalog'],
    queryFn: () =>
      apiFetch<{ entries: import('@marketing-hub/shared').AcquisitionCatalogEntry[] }>(
        '/api/scraper/acquisition-catalog',
      ),
  });

  const jobsQ = useQuery({
    queryKey: ['scrape-jobs', projectId],
    enabled: Boolean(projectId),
    queryFn: () => apiFetch<{ jobs: ScrapeJob[] }>(`/api/scrape-jobs?project_id=${projectId}`),
    refetchInterval: (query) => {
      const jobs = query.state.data?.jobs ?? [];
      const activeRun = jobs.some((j) => j.status === 'queued' || j.status === 'running');
      const enriching = jobs.some((j) => {
        const st = j.config?.website_enrich_status;
        return st === 'queued' || st === 'running';
      });
      return activeRun || enriching ? 5000 : false;
    },
  });

  const candidatesQ = useQuery({
    queryKey: ['scrape-candidates', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ candidates: ScrapeCandidate[] }>(
        `/api/scrape-candidates?project_id=${projectId}&status=pending`,
      ),
    refetchInterval: (query) => {
      const jobs = jobsQ.data?.jobs ?? [];
      const active = jobs.some((j) => j.status === 'queued' || j.status === 'running');
      const pending = (query.state.data?.candidates?.length ?? 0) > 0;
      return active || pending ? 8000 : false;
    },
  });

  const blocklistQ = useQuery({
    queryKey: ['scrape-blocklist', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ blocklist: { id: string; domain_normalized: string; note: string | null }[] }>(
        `/api/scrape-blocklist?project_id=${projectId}`,
      ),
  });

  const healthQ = useQuery({
    queryKey: ['data-health', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ summary: DataHealthSummary }>(`/api/data-health/summary?project_id=${projectId}`),
  });

  const inboundQ = useQuery({
    queryKey: ['inbound-requests', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{
        requests: {
          id: string;
          status_code: number | null;
          spam_score: string | null;
          lead_id: string | null;
          created_at: string;
        }[];
      }>(`/api/inbound-requests?project_id=${projectId}`),
  });

  const createJob = useMutation({
    mutationFn: async () => {
      const config = buildScrapeJobConfig(jobForm);
      const res = await apiFetch<{ job: { id: string } }>('/api/scrape-jobs', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          name: jobName,
          config,
          respect_robots: jobForm.jobSource === 'urls',
          rate_limit_ms: 2000,
          audience_id: audienceId || null,
        }),
      });
      if (runAfterCreate && res.job?.id) {
        await apiFetch(`/api/scrape-jobs/${res.job.id}/run`, { method: 'POST', body: '{}' });
      }
      return res;
    },
    onSuccess: () => {
      setJobName('');
      setJobForm(initialScrapeJobFormState({ seedUrls: 'https://example.com/contact\n' }));
      void qc.invalidateQueries({ queryKey: ['scrape-jobs', projectId] });
      void qc.invalidateQueries({ queryKey: ['scrape-candidates', projectId] });
    },
  });

  const runJob = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/scrape-jobs/${id}/run`, { method: 'POST', body: '{}' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['scrape-jobs', projectId] }),
  });

  const approve = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/scrape-candidates/${id}/approve`, { method: 'POST', body: '{}' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['scrape-candidates', projectId] });
      void qc.invalidateQueries({ queryKey: ['data-health', projectId] });
      void qc.invalidateQueries({ queryKey: ['audience-scraper-funnel'] });
      void qc.invalidateQueries({ queryKey: ['audiences'] });
    },
  });

  const reject = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/scrape-candidates/${id}/reject`, { method: 'POST', body: '{}' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['scrape-candidates', projectId] }),
  });

  const addBlock = useMutation({
    mutationFn: () =>
      apiFetch('/api/scrape-blocklist', {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId, domain: blockDomain }),
      }),
    onSuccess: () => {
      setBlockDomain('');
      void qc.invalidateQueries({ queryKey: ['scrape-blocklist', projectId] });
    },
  });

  const caps = scrapeCapsQ.data;
  const health = healthQ.data?.summary;
  const candidates = candidatesQ.data?.candidates ?? [];
  const jobs = jobsQ.data?.jobs ?? [];

  return (
    <ProjectGate requireSelection pageTitle="Acquisitie & data">
      {!projectId ? null : jobsQ.isLoading ? (
        <LoadingState />
      ) : (
        <div className={scraperStyles.page}>
          <header className={scraperStyles.hero}>
            <div>
              <h1 className={scraperStyles.heroTitle}>Acquisitie &amp; datakwaliteit</h1>
              <p className={scraperStyles.heroLead}>
                Start website- of Apify-runs, keur kandidaten goed vóór ze in CRM komen, en houd
                e-mailverificatie en stale leads in de gaten.
              </p>
            </div>
            <div className={scraperStyles.chips}>
              <span
                className={`${scraperStyles.chip} ${caps?.apify.configured ? scraperStyles.chipOk : ''}`}
                title={
                  caps?.apify.configured
                    ? 'Apify API-token geconfigureerd'
                    : 'Zet APIFY_API_TOKEN in server-.env'
                }
              >
                Apify {caps?.apify.configured ? 'actief' : 'niet geconfigureerd'}
              </span>
              <span
                className={`${scraperStyles.chip} ${caps?.fetch.playwright_enabled ? scraperStyles.chipOk : scraperStyles.chipOff}`}
              >
                Playwright {caps?.fetch.playwright_enabled ? 'aan' : 'uit'}
              </span>
            </div>
          </header>

          <div className={dashboardStyles.grid}>
            <DashboardKpiCard label="Leads in project" value={health?.total ?? '—'} />
            <DashboardKpiCard
              label="Review queue"
              value={health?.pending_scrape ?? candidates.length}
              warn={(health?.pending_scrape ?? candidates.length) > 0}
            />
            <DashboardKpiCard
              label="Te verifiëren (e-mail)"
              value={health?.unknown_verify ?? '—'}
              warn={(health?.unknown_verify ?? 0) > 0}
            />
            <DashboardKpiCard
              label="Stale 90d+"
              value={health?.stale ?? '—'}
              warn={(health?.stale ?? 0) > 0}
            />
            <DashboardKpiCard
              label="Ongeldig / risky e-mail"
              value={health?.invalid_email ?? '—'}
              warn={(health?.invalid_email ?? 0) > 0}
            />
          </div>

          <div className={scraperStyles.mainColumns}>
            <div>
              <section className={`${dashboardStyles.card} ${scraperStyles.formWide}`}>
                <div className={scraperStyles.sectionHead}>
                  <h2>Nieuwe acquisitie-run</h2>
                </div>
                <p className={scraperStyles.sectionHint}>
                  Kies bron, vul segmentatie in en start direct — jobs verschijnen hieronder met
                  live status.
                </p>

                <div className={scraperStyles.sourceTabs} role="tablist" aria-label="Scrape bron">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={jobForm.jobSource === 'google_places'}
                    className={`${scraperStyles.sourceTab} ${jobForm.jobSource === 'google_places' ? scraperStyles.sourceTabActive : ''}`}
                    onClick={() => setJobForm((f) => ({ ...f, jobSource: 'google_places' }))}
                    disabled={!scrapeCapsQ.data?.google_places?.configured}
                    title={
                      scrapeCapsQ.data?.google_places?.configured
                        ? 'Aanbevolen'
                        : 'GOOGLE_PLACES_API_KEY vereist'
                    }
                  >
                    Google Places API
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={jobForm.jobSource === 'google_serp'}
                    className={`${scraperStyles.sourceTab} ${jobForm.jobSource === 'google_serp' ? scraperStyles.sourceTabActive : ''}`}
                    onClick={() => setJobForm((f) => ({ ...f, jobSource: 'google_serp' }))}
                    disabled={!scrapeCapsQ.data?.google_serp?.configured}
                    title={
                      scrapeCapsQ.data?.google_serp?.configured
                        ? 'Keyword search via Custom Search API'
                        : 'GOOGLE_CSE_CX + API-key vereist'
                    }
                  >
                    Google Search (SERP)
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={jobForm.jobSource === 'apify'}
                    className={`${scraperStyles.sourceTab} ${jobForm.jobSource === 'apify' ? scraperStyles.sourceTabActive : ''}`}
                    onClick={() => setJobForm((f) => ({ ...f, jobSource: 'apify' }))}
                  >
                    Apify (fallback)
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={jobForm.jobSource === 'urls'}
                    className={`${scraperStyles.sourceTab} ${jobForm.jobSource === 'urls' ? scraperStyles.sourceTabActive : ''}`}
                    onClick={() => setJobForm((f) => ({ ...f, jobSource: 'urls' }))}
                  >
                    Website (seed URLs)
                  </button>
                </div>

                <form
                  className={projectStyles.formGrid}
                  onSubmit={(e: FormEvent) => {
                    e.preventDefault();
                    createJob.mutate();
                  }}
                >
                  <Field label="Naam">
                    <TextInput
                      value={jobName}
                      onChange={(e) => setJobName(e.target.value)}
                      placeholder="Bijv. NL horeca Amsterdam Q4"
                      required
                    />
                  </Field>
                  <ScrapeJobSourceFields
                    form={jobForm}
                    onChange={(patch) => setJobForm((f) => ({ ...f, ...patch }))}
                    scrapeCaps={scrapeCapsQ.data}
                    catalogEntries={catalogQ.data?.entries}
                    hideSourceSelect
                  />
                  <label className={projectStyles.mutedSmall}>
                    <input
                      type="checkbox"
                      checked={runAfterCreate}
                      onChange={(e) => setRunAfterCreate(e.target.checked)}
                    />{' '}
                    Direct runnen na aanmaken
                  </label>
                  {audiencesOn ? (
                    <Field label="Koppel aan doelgroep (optioneel)">
                      <select
                        className={leadStyles.filterInput}
                        value={audienceId}
                        onChange={(e) => setAudienceId(e.target.value)}
                      >
                        <option value="">— geen —</option>
                        {(audiencesQ.data?.audiences ?? []).map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ) : null}
                  <Button
                    type="submit"
                    disabled={createJob.isPending || !isScrapeJobFormValid(jobForm) || !jobName.trim()}
                  >
                    {createJob.isPending ? 'Bezig…' : 'Run starten'}
                  </Button>
                  {createJob.isError ? (
                    <p className={projectStyles.mutedSmall}>
                      Job aanmaken mislukt — controleer config, Apify-token of API.
                    </p>
                  ) : null}
                </form>
              </section>

              <section className={`${dashboardStyles.card} ${scraperStyles.formWide}`}>
                <div className={scraperStyles.sectionHead}>
                  <h2>Jobs</h2>
                  <span className={projectStyles.mutedSmall}>{jobs.length} totaal</span>
                </div>
                {!jobs.length ? (
                  <p className={projectStyles.mutedSmall}>
                    Nog geen runs — start hierboven je eerste acquisitie-job.
                  </p>
                ) : (
                  <ul className={projectStyles.list} style={{ gap: 'var(--space-sm)' }}>
                    {jobs.map((j) => (
                      <li key={j.id} className={scraperStyles.jobRow}>
                        <div className={scraperStyles.jobRowTop}>
                          <strong>{j.name}</strong>
                          <ScraperStatusBadge status={j.status} />
                        </div>
                        <div className={scraperStyles.jobMeta}>
                          {jobSourceLabel(j)}
                          {j.config?.apify_last_imported != null
                            ? ` · ${j.config.apify_last_imported} geïmporteerd`
                            : ''}
                          {j.pending_count ? ` · ${j.pending_count} in review` : ''}
                          {j.config?.website_enrich_status
                            ? ` · e-mail scan: ${j.config.website_enrich_status}`
                            : ''}
                          {j.config?.website_enrich_updated != null
                            ? ` (${j.config.website_enrich_updated} met mail)`
                            : ''}
                        </div>
                        {j.last_error ? (
                          <div className={projectStyles.mutedSmall}>{j.last_error}</div>
                        ) : null}
                        <div>
                          <Button
                            variant="ghost"
                            disabled={
                              !['draft', 'completed', 'failed', 'paused'].includes(j.status) ||
                              runJob.isPending
                            }
                            onClick={() => runJob.mutate(j.id)}
                          >
                            Opnieuw runnen
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            <aside>
              <section
                className={`${dashboardStyles.card} ${candidates.length ? scraperStyles.reviewHighlight : ''}`}
              >
                <div className={scraperStyles.sectionHead}>
                  <h2>Review queue</h2>
                  <span className={scraperStyles.reviewCount}>{candidates.length}</span>
                </div>
                <p className={scraperStyles.sectionHint}>
                  Goedkeuren maakt een lead aan en triggert enrichment; afkeuren verwijdert de
                  kandidaat uit de wachtrij.
                </p>
                {!candidates.length ? (
                  <p className={projectStyles.mutedSmall}>
                    Geen kandidaten — na een run verschijnen ze hier.
                  </p>
                ) : (
                  <ul className={projectStyles.list} style={{ gap: 'var(--space-sm)' }}>
                    {candidates.map((c) => (
                      <li key={c.id} className={scraperStyles.candidateCard}>
                        <div className={scraperStyles.candidateTop}>
                          <div>
                            <strong>{c.company ?? c.email ?? 'Kandidaat'}</strong>
                            {c.contact_name ? (
                              <div className={projectStyles.mutedSmall}>{c.contact_name}</div>
                            ) : null}
                          </div>
                          <span className={candidateScoreClass(c.confidence_score)}>
                            {c.confidence_score}
                          </span>
                        </div>
                        <div className={projectStyles.mutedSmall}>
                          {c.email ? <div>{c.email}</div> : null}
                          {c.website ? (
                            <div>
                              <a href={c.website} target="_blank" rel="noreferrer">
                                {c.website.replace(/^https?:\/\//, '')}
                              </a>
                            </div>
                          ) : null}
                          {c.source_url ? (
                            <div>
                              Bron:{' '}
                              <a href={c.source_url} target="_blank" rel="noreferrer">
                                pagina
                              </a>
                            </div>
                          ) : null}
                        </div>
                        <div className={scraperStyles.candidateActions}>
                          <Button variant="ghost" onClick={() => approve.mutate(c.id)}>
                            Goedkeuren
                          </Button>
                          <Button variant="ghost" onClick={() => reject.mutate(c.id)}>
                            Afkeuren
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </aside>
          </div>

          <div className={scraperStyles.secondaryGrid}>
            <section className={dashboardStyles.card}>
              <div className={scraperStyles.sectionHead}>
                <h2>Do-not-scrape</h2>
              </div>
              <form
                className={projectStyles.formGrid}
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  addBlock.mutate();
                }}
              >
                <Field label="Domein">
                  <TextInput
                    value={blockDomain}
                    onChange={(e) => setBlockDomain(e.target.value)}
                    placeholder="concurrent.nl"
                    required
                  />
                </Field>
                <Button type="submit" disabled={addBlock.isPending}>
                  Blokkeren
                </Button>
              </form>
              <ul className={projectStyles.list}>
                {(blocklistQ.data?.blocklist ?? []).map((b) => (
                  <li key={b.id} className={projectStyles.mutedSmall}>
                    {b.domain_normalized}
                    {b.note ? ` — ${b.note}` : ''}
                  </li>
                ))}
              </ul>
            </section>

            <section className={dashboardStyles.card}>
              <div className={scraperStyles.sectionHead}>
                <h2>Inbound log</h2>
                <span className={projectStyles.mutedSmall}>Recent</span>
              </div>
              <ul className={projectStyles.list}>
                {(inboundQ.data?.requests ?? []).slice(0, 15).map((r) => (
                  <li key={r.id} className={projectStyles.mutedSmall}>
                    {new Date(r.created_at).toLocaleString()} — HTTP {r.status_code ?? '?'}
                    {r.spam_score != null ? ` · spam ${r.spam_score}` : ''}
                    {r.lead_id ? ` · lead ${r.lead_id.slice(0, 8)}…` : ''}
                  </li>
                ))}
              </ul>
              {!inboundQ.data?.requests?.length ? (
                <p className={projectStyles.mutedSmall}>Nog geen inbound requests.</p>
              ) : null}
            </section>
          </div>
        </div>
      )}
    </ProjectGate>
  );
}
